// Setup › Structure › Assessments at `/c/{slug}/setup/assessments` (M8-ASSESS, managers). The
// round and its view are in the URL (`?round=&view=progress|report|chart`), so the Home card's
// link and a reload open the same place.
import { createRoute, useNavigate } from "@tanstack/react-router";
import { AssessmentsScreen, type AssessView } from "../features/assess/assessments-screen";
import { isManager } from "../features/company";
import { useCompany } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { NoAccessPage } from "../pages/status-pages";
import { setupRoute } from "./c.$slug.setup";

const ID = /^[0-9a-f-]{36}$/i;
const VIEWS: readonly AssessView[] = ["progress", "report", "chart"];

export const setupAssessmentsRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "assessments",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.round === "string" && ID.test(search.round) ? { round: search.round } : {}),
    ...(typeof search.view === "string" && VIEWS.includes(search.view as AssessView)
      ? { view: search.view as AssessView }
      : {}),
  }),
  component: function Assessments() {
    const { api } = setupAssessmentsRoute.useRouteContext();
    const { round, view } = setupAssessmentsRoute.useSearch();
    const company = useCompany();
    const navigate = useNavigate();
    if (!isManager(company)) return <NoAccessPage what="Assessments" slug={company.slug} />;
    const go = (next: { round?: string | undefined; view?: AssessView | undefined }) =>
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
      } as never);
    return (
      <AssessmentsScreen
        api={api}
        company={company}
        roundId={round}
        view={view ?? "progress"}
        onRound={(id, v) => go({ round: id, view: id ? v : undefined })}
        onView={(v) => go({ view: v })}
      />
    );
  },
});
